// frontend/src/pages/builder/builderSpec.js
// Pure rules for turning field clicks into a query spec (no React, unit-tested).
// The backend compiler is the authority; these rules only keep the builder from
// producing specs it would reject (json fields, sum of text, duplicates, too
// many dimensions), so the user sees a working chart instead of an error.
//
// Step 13c-3: a dataset's saved measures (Model page > Calculations) are offered
// next to its fields. Each is carried as a field-shaped object with role
// "metric" (measureField), so every list, chip and label works on it unchanged;
// in the spec it is { aggregation: "metric", field: "_metric_<name>" }, and it
// can only ever be a value, never a grouping or a filter. A calculated column
// arrives from the server as an ordinary field (calculated: true).

export const MAX_DIMENSIONS = 3;         // a chart: mirrors MAX_CHART_DIMENSIONS in app/semantic/spec.py
export const MAX_TABLE_DIMENSIONS = 20;  // a table (7d-2, decision D11): mirrors MAX_DIMENSIONS there
export const MAX_MEASURES = 10;
const NUMERIC = ['integer', 'number'];
const STRUCTURED = ['json_array', 'json_object'];

export const METRIC = 'metric';              // the role of a saved measure in the builder, and its aggregation in a spec

export const GROUPS = [
  { role: 'time', label: 'Time' },
  { role: 'dimension', label: 'Dimensions' },
  { role: 'measure', label: 'Measures' },
  { role: METRIC, label: 'Saved measures' },
  { role: 'identifier', label: 'Identifiers' },
];

/** A saved measure as the builder carries it: field-shaped, role "metric", always a number. */
export function measureField(m) {
  return {
    key: m.key, display_name: m.display_name || m.name || m.key, data_type: 'number', role: METRIC,
    is_custom: false, measure: true, format: m.format || null, description: m.description || null,
    sample_value: null, null_ratio: 0, default_aggregation: null, default_grain: null,
  };
}

export const isMeasureField = (field) => Boolean(field) && field.role === METRIC;

/** The fields the builder works with, from GET /semantic/datasets/{id}/fields: the fields, then the saved measures. */
export function fieldsFromResponse(body) {
  const fields = (body && body.fields) || [];
  const measures = ((body && body.measures) || []).filter((m) => m && typeof m.key === 'string' && m.key.startsWith('_metric_'));
  return [...fields, ...measures.map(measureField)];
}

/** Fields the builder can offer: not hidden, not structured json. */
export function chartableFields(fields) {
  return (fields || []).filter((f) => f.role !== 'ignore' && !STRUCTURED.includes(f.data_type));
}

/** Fields grouped by role (Time, Dimensions, Measures, Identifiers), filtered by a search term. */
export function groupFields(fields, query = '') {
  const q = query.trim().toLowerCase();
  const match = (f) => !q || String(f.display_name || '').toLowerCase().includes(q) || String(f.key).toLowerCase().includes(q);
  const usable = chartableFields(fields).filter(match);
  return GROUPS
    .map((g) => ({ ...g, fields: usable.filter((f) => (GROUPS.some((x) => x.role === f.role) ? f.role : 'dimension') === g.role) }))
    .filter((g) => g.fields.length > 0);
}

/**
 * What clicking a field adds. Time -> a dimension by its default grain (month
 * unless the Model view set another); dimension -> a dimension; numeric
 * measure -> its default aggregation (sum); identifier -> count distinct.
 */
export function defaultUse(field) {
  if (isMeasureField(field)) return { kind: 'measure', item: { aggregation: METRIC, field: field.key } };
  if (field.role === 'time') return { kind: 'dimension', item: { field: field.key, grain: startingGrain(field) } };
  if (field.role === 'identifier') return { kind: 'measure', item: { aggregation: startingAggregation(field), field: field.key } };
  if (field.role === 'measure' && NUMERIC.includes(field.data_type)) {
    return { kind: 'measure', item: { aggregation: startingAggregation(field), field: field.key } };
  }
  return { kind: 'dimension', item: { field: field.key } };
}

export const sameMeasure = (a, b) => a.aggregation === b.aggregation && (a.field || null) === (b.field || null);

/**
 * Add a field to the draft. Returns { draft, notice }: notice explains why
 * nothing changed (already added, limit reached); draft is unchanged then.
 */
export function addField(draft, field) {
  const use = defaultUse(field);
  if (use.kind === 'dimension') {
    if (draft.dimensions.some((d) => d.field === use.item.field)) return { draft, notice: `${field.display_name} is already in the chart.` };
    if (draft.dimensions.length >= MAX_DIMENSIONS) return { draft, notice: `A chart can group by up to ${MAX_DIMENSIONS} fields.` };
    return { draft: { ...draft, dimensions: [...draft.dimensions, use.item] }, notice: null };
  }
  if (draft.measures.some((m) => sameMeasure(m, use.item))) return { draft, notice: `${field.display_name} is already in the chart.` };
  if (draft.measures.length >= MAX_MEASURES) return { draft, notice: `A chart can show up to ${MAX_MEASURES} values.` };
  return { draft: { ...draft, measures: [...draft.measures, use.item] }, notice: null };
}

export const emptyDraft = () => ({ dimensions: [], measures: [] });

/** The spec sent to the server. No measure chosen yet means "count of records". */
export function toSpec(datasetId, draft) {
  if (!datasetId) return null;
  return {
    dataset_id: datasetId,
    dimensions: draft.dimensions,
    measures: draft.measures.length ? draft.measures : [{ aggregation: 'count' }],
  };
}

/** Preview type until the type switcher exists: number, trend over time, or bars. */
export function defaultType(draft, fieldsByKey = {}) {
  if (draft.dimensions.length === 0) return 'card';
  const first = fieldsByKey[draft.dimensions[0].field];
  return first && first.role === 'time' ? 'line' : 'bar';
}

// ---------------------------------------------------------------------------
// Wells (6c-1): X axis = first dimension, Group by = the rest, Values = measures
// ---------------------------------------------------------------------------

const AGG_LABEL = { count: 'Count', count_distinct: 'Distinct', sum: 'Sum of', avg: 'Average', min: 'Min', max: 'Max' };

function fieldName(key, fieldsByKey) {
  const f = fieldsByKey[key];
  return (f && f.display_name) || key;
}

export function measureLabel(m, fieldsByKey = {}) {
  if (m.aggregation === 'count') return 'Count of records';
  if (m.aggregation === METRIC) return fieldName(m.field, fieldsByKey);        // a saved measure is its own name
  return `${AGG_LABEL[m.aggregation] || m.aggregation} ${fieldName(m.field, fieldsByKey)}`;
}

export function dimensionLabel(d, fieldsByKey = {}) {
  return d.grain ? `${fieldName(d.field, fieldsByKey)} · ${d.grain}` : fieldName(d.field, fieldsByKey);
}

/** The three wells as chips: { x, groupBy, values }, each chip carrying where it lives in the draft. */
export function wellsOf(draft, fieldsByKey = {}) {
  const dims = draft.dimensions.map((d, index) => ({ kind: 'dimension', index, item: d, label: dimensionLabel(d, fieldsByKey) }));
  return {
    x: dims.slice(0, 1),
    groupBy: dims.slice(1),
    values: draft.measures.map((m, index) => ({ kind: 'measure', index, item: m, label: measureLabel(m, fieldsByKey) })),
  };
}

/** Remove one chip. Removing the X axis promotes the first Group by field to X. */
export function removeItem(draft, kind, index) {
  if (kind === 'dimension') return { ...draft, dimensions: draft.dimensions.filter((_, i) => i !== index) };
  return { ...draft, measures: draft.measures.filter((_, i) => i !== index) };
}

// ---------------------------------------------------------------------------
// 6c-2: drop a field on a well; change a value's aggregation or a date's grain
// ---------------------------------------------------------------------------

export const GRAINS = ['day', 'week', 'month', 'quarter', 'year'];
const DATES = ['date', 'datetime'];

/** Aggregations the compiler accepts for this field (count of records is not tied to a field). */
export function aggregationsFor(field) {
  if (!field) return [];
  if (isMeasureField(field)) return [METRIC];           // one choice: no picker on its chip
  if (NUMERIC.includes(field.data_type)) return ['sum', 'avg', 'min', 'max', 'count_distinct'];
  if (DATES.includes(field.data_type)) return ['count_distinct', 'min', 'max'];
  return ['count_distinct'];
}

export const AGGREGATION_LABELS = {
  sum: 'Sum', avg: 'Average', min: 'Min', max: 'Max', count_distinct: 'Distinct count', [METRIC]: 'Measure',
};

/** Why a saved measure cannot go where it was dropped (a grouping or a filter), or null. */
export function measureOnlyNotice(field) {
  return isMeasureField(field) ? `${field.display_name} is a measure: it can only be a value.` : null;
}

export function dimensionItem(field) {
  return field.role === 'time' || DATES.includes(field.data_type) ? { field: field.key, grain: startingGrain(field) } : { field: field.key };
}

// Step 13b: the Model view lets an admin set, per field, how it starts out in a chart
// (field.default_aggregation, field.default_grain; null = automatic). The builder only
// uses a default that fits what it offers for the field, so a stale or odd value falls
// back to the automatic one instead of making a chart the compiler would refuse.

/** The aggregation a field starts with as a value: the Model view's default if it fits, else distinct count for an identifier, else Sum for a number and distinct count for the rest. */
export function startingAggregation(field) {
  const options = aggregationsFor(field);
  if (field.default_aggregation && options.includes(field.default_aggregation)) return field.default_aggregation;
  if (field.role === 'identifier') return 'count_distinct';
  return options[0];
}

/** The grain a date field starts with: the Model view's default, else month. */
export function startingGrain(field) {
  return GRAINS.includes(field.default_grain) ? field.default_grain : 'month';
}

/**
 * Drop a field on a well ('x' | 'groupBy' | 'values'). X replaces the first
 * dimension (the old X moves to Group by); a field already used as a
 * dimension is moved, not duplicated. Returns { draft, notice }.
 */
export function dropField(draft, field, well) {
  if (well !== 'values' && isMeasureField(field)) return { draft, notice: measureOnlyNotice(field) };
  if (well === 'values') {
    const item = { aggregation: startingAggregation(field), field: field.key };
    if (draft.measures.some((m) => sameMeasure(m, item))) return { draft, notice: `${field.display_name} is already in the chart.` };
    if (draft.measures.length >= MAX_MEASURES) return { draft, notice: `A chart can show up to ${MAX_MEASURES} values.` };
    return { draft: { ...draft, measures: [...draft.measures, item] }, notice: null };
  }
  const rest = draft.dimensions.filter((d) => d.field !== field.key);
  const existing = draft.dimensions.find((d) => d.field === field.key);
  const item = existing || dimensionItem(field);
  let dimensions;
  if (well === 'x') {
    dimensions = [item, ...rest];
  } else {
    if (draft.dimensions.length && draft.dimensions[0].field === field.key && rest.length === 0) {
      return { draft, notice: 'Add a different field to the X axis first.' };
    }
    dimensions = rest.length ? [rest[0], ...rest.slice(1), item] : [item];
  }
  if (dimensions.length > MAX_DIMENSIONS) return { draft, notice: `A chart can group by up to ${MAX_DIMENSIONS} fields.` };
  return { draft: { ...draft, dimensions }, notice: null };
}

export function setAggregation(draft, index, aggregation) {
  const current = draft.measures[index];
  if (!current) return { draft, notice: null };
  const next = { ...current, aggregation };
  if (draft.measures.some((m, i) => i !== index && sameMeasure(m, next))) {
    return { draft, notice: 'That value is already in the chart.' };
  }
  return { draft: { ...draft, measures: draft.measures.map((m, i) => (i === index ? next : m)) }, notice: null };
}

export function setGrain(draft, index, grain) {
  if (!GRAINS.includes(grain) || !draft.dimensions[index]) return draft;
  return { ...draft, dimensions: draft.dimensions.map((d, i) => (i === index ? { ...d, grain } : d)) };
}

/** Short type marker shown next to a field: Aa text, # number, Dt date, T/F yes-no. */
export function typeMarker(field) {
  if (isMeasureField(field)) return 'Σ';
  if (NUMERIC.includes(field.data_type)) return '#';
  if (DATES.includes(field.data_type)) return 'Dt';
  if (field.data_type === 'boolean') return 'T/F';
  return 'Aa';
}

/**
 * Move a chip that is already in a well onto another well. A dimension moved
 * to Values becomes a value (e.g. distinct count); a value moved to X axis or
 * Group by becomes a grouping. If the move is not allowed, nothing changes
 * and the notice says why. Returns { draft, notice }.
 */
export function moveChip(draft, kind, index, field, well) {
  if (!field) return { draft, notice: null };
  if (kind === 'measure' && well === 'values') return { draft, notice: null };
  if (kind === 'dimension' && well !== 'values') return dropField(draft, field, well);
  const result = dropField(removeItem(draft, kind, index), field, well);
  return result.notice ? { draft, notice: result.notice } : result;
}
